import React, { useEffect, useRef, useState } from "react";
import { Avatar, Image, Input, Spin } from "antd";
import { CloseOutlined, PaperClipOutlined } from "@ant-design/icons";
import ConfirmationModal from "../../../components/global/modal/ConfirmationModal.jsx";
import {
  GetTicketByIdApi,
  GetTicketChatApi,
  UpdateTicketApi,
} from "../../../api/request/ticket";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

const getMessageImages = (msg) => {
  const source =
    msg?.fileName ??
    msg?.fileUrl ??
    msg?.fileUrls ??
    msg?.files ??
    msg?.attachments;
  if (!source) return [];

  const list = Array.isArray(source) ? source : [source];
  return list
    .map((item) => {
      if (typeof item === "string") return item;
      return item?.url || item?.fileUrl || item?.fileName || item?.path || null;
    })
    .filter(Boolean);
};

function ViewTicket() {
  const { id } = useParams();
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--theme")
    ?.trim();
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const userDetails = useSelector((state) => state.user.userDetails);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [ticket, setTicket] = useState({});
  const [ticketChat, setTicketChat] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [closeTicketConfirmation, setCloseTicketConfirmation] = useState(false);
  const [isCloseTicketLoading, setIsCloseTicketLoading] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [ticketChat]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const clearSelectedFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      e.target.value = "";
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const getTicketById = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetTicketByIdApi(id);
      if (data?.success) {
        setTicket(data?.data);
      }
    } catch (error) {
      // keep silent
    } finally {
      setIsLoading(false);
    }
  };

  const getTicketChat = async () => {
    setIsChatLoading(true);
    try {
      const formData = new FormData();
      formData.append("TicketId", id);
      formData.append("UserComments", "");
      formData.append("AdminComments", "");
      const { data } = await GetTicketChatApi(formData);
      if (data?.success) {
        setTicketChat(data?.data?.comments || []);
      }
    } catch (error) {
      // keep silent
    } finally {
      setIsChatLoading(false);
    }
  };

  useEffect(() => {
    getTicketById();
    getTicketChat();
  }, []);

  const sendMessage = async () => {
    const trimmedMessage = message?.trim();
    if (!trimmedMessage && !selectedFile) return;

    try {
      setIsSending(true);
      const formData = new FormData();
      formData.append("TicketId", id);
      formData.append("UserComments", "");
      formData.append("AdminComments", trimmedMessage || "");
      if (selectedFile) {
        formData.append("FileName", selectedFile);
      }

      const { data } = await GetTicketChatApi(formData);
      if (data?.success) {
        setMessage("");
        clearSelectedFile();
        setTicketChat(data?.data?.comments || []);
      } else {
        toast.error(data?.message || "Failed to send message.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to send message.",
      );
    } finally {
      setIsSending(false);
    }
  };

  const closeTicketHandler = async () => {
    try {
      setIsCloseTicketLoading(true);
      const params = {
        TicketId: ticket?.ticketId,
        UserId: ticket?.userId,
        AdminComments: "Closed by Admin",
        TicketStatusId: 3,
      };
      const { data } = await UpdateTicketApi(params);
      if (data?.success) {
        toast.success(data?.message);
        setCloseTicketConfirmation(false);
        getTicketById();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error?.message || "Failed to close ticket.",
      );
    } finally {
      setIsCloseTicketLoading(false);
    }
  };

  const getInitials = (name) => {
    const nameParts = String(name || "U").trim().split(" ");
    const initials =
      (nameParts[0]?.[0] || "U") +
      (nameParts[nameParts.length - 1]?.[0] || "");
    return initials?.toUpperCase();
  };

  const renderChatBubble = (chatMessage, isOwnMessage) => {
    const images = getMessageImages(chatMessage);
    const text = isOwnMessage
      ? chatMessage?.adminComments || ""
      : chatMessage?.userComments ||
        chatMessage?.adminComments ||
        "";

    return (
      <div
        className={`bg-primary-light text-[#2F2B3D] rounded-b-md font-medium py-2 px-4 ${
          isOwnMessage ? "rounded-tl-md" : "rounded-tr-md"
        }`}
      >
        {text ? <p className="m-0 whitespace-pre-wrap">{text}</p> : null}
        {images.length > 0 ? (
          <div className={`${text ? "mt-2" : ""} flex flex-wrap gap-2`}>
            <Image.PreviewGroup>
              {images.map((url, imgIndex) => (
                <Image
                  key={`${url}-${imgIndex}`}
                  src={url}
                  alt="attachment"
                  className="rounded-md object-cover"
                  style={{ maxWidth: 220, maxHeight: 220 }}
                />
              ))}
            </Image.PreviewGroup>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div>
      {isLoading || isChatLoading ? (
        <div className="flex justify-center flex-col mt-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <div>
          <div className="border-[2px] border-light rounded-xl pt-4 w-full">
            <div className="px-4 lg:px-6 pb-4 border-b border-light flex flex-wrap justify-between items-center">
              <p className="text-[#2F2B3D] text-title font-semibold">
                Ticket - #{ticket?.ticketNo}
              </p>
              {ticket?.status == "Open" && (
                <button
                  className="bg-primary text-dex text-white font-medium uppercase px-6 py-2 rounded-lg themeHover duration-500"
                  onClick={() => setCloseTicketConfirmation(true)}
                >
                  Close Ticket
                </button>
              )}
            </div>
            <div className="lg:p-6 p-4">
              <p className="primary text-title font-semibold">{ticket?.title}</p>
              <p className="primary text-description mt-2">
                {ticket?.description}
              </p>
            </div>
          </div>

          <div className="border-[2px] border-light rounded-xl pt-4 w-full mt-5">
            <div className="flex items-center px-4 lg:px-6 pb-4 border-b border-light">
              <Avatar
                size={40}
                className="bg-primary uppercase"
                src={ticket?.profileImage}
              >
                {getInitials(`${ticket?.firstName} ${ticket?.lastName}`)}
              </Avatar>
              <p className="font-semibold text-base ml-2 text-[#2F2B3D]">
                {ticket?.firstName} {ticket?.lastName}
              </p>
            </div>

            <div className="max-h-[500px] min-h-[200px] overflow-y-auto">
              {ticketChat?.length > 0 ? (
                <div className="px-4 lg:px-6">
                  {ticketChat?.map((chatMessage, index) => {
                    const isOwnMessage =
                      userDetails?._id === chatMessage?.userId;
                    return (
                      <React.Fragment
                        key={
                          chatMessage?.id ||
                          chatMessage?.chatId ||
                          `${chatMessage?.createdDate}-${index}`
                        }
                      >
                        {!isOwnMessage ? (
                          <div className="flex w-full my-6">
                            <Avatar
                              size={40}
                              className="bg-primary uppercase"
                              src={chatMessage?.profileImage}
                            >
                              {getInitials(`${chatMessage?.name}`)}
                            </Avatar>
                            <div className="w-[70%] md:w-[50%] lg:w-[40%] ml-3">
                              <div className="flex justify-between mb-1">
                                <p className="text-sm text-[#2F2B3D] font-medium">
                                  {chatMessage?.name}
                                </p>
                                <p className="text-sm text-[#2F2B3D] opacity-70 font-medium">
                                  {moment(chatMessage?.createdDate).format(
                                    "DD-MM-YYYY LT",
                                  )}
                                </p>
                              </div>
                              {renderChatBubble(chatMessage, false)}
                            </div>
                          </div>
                        ) : null}

                        {isOwnMessage ? (
                          <div className="flex justify-end w-full my-6">
                            <div className="w-[70%] md:w-[50%] lg:w-[40%] ml-3">
                              <div className="flex justify-between mb-1">
                                <p className="text-sm text-[#2F2B3D] font-medium">
                                  {chatMessage?.name}
                                </p>
                                <p className="text-sm text-[#2F2B3D] opacity-70 font-medium">
                                  {moment(chatMessage?.createdDate).format(
                                    "DD-MM-YYYY LT",
                                  )}
                                </p>
                              </div>
                              {renderChatBubble(chatMessage, true)}
                            </div>
                          </div>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="p-4 lg:p-6">
                  <p className="opacity-50 primary">
                    Start new conversation with {ticket?.firstName}{" "}
                    {ticket?.lastName} ...
                  </p>
                </div>
              )}
            </div>

            {ticket?.status === "Open" && (
              <div className="p-4 lg:p-6">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {previewUrl ? (
                  <div className="mb-2 inline-flex items-start relative">
                    <img
                      src={previewUrl}
                      alt="Selected attachment"
                      className="h-16 w-16 object-cover rounded-md border border-light"
                    />
                    <button
                      type="button"
                      onClick={clearSelectedFile}
                      className="absolute -top-2 -right-2 bg-white border border-light rounded-full w-5 h-5 flex items-center justify-center shadow-sm"
                      aria-label="Remove attachment"
                    >
                      <CloseOutlined style={{ fontSize: 10 }} />
                    </button>
                  </div>
                ) : null}
                <Input
                  className="transperant-input py-2 rounded-lg search-bar text-base w-full"
                  placeholder="Type a message..."
                  value={message}
                  disabled={isSending}
                  onChange={(e) => setMessage(e.target.value)}
                  onPressEnter={sendMessage}
                  prefix={
                    <PaperClipOutlined
                      onClick={() =>
                        !isSending && fileInputRef.current?.click()
                      }
                      className="cursor-pointer text-lg"
                      style={{ color: primaryColor }}
                    />
                  }
                  suffix={
                    isSending ? (
                      <Spin size="small" />
                    ) : (
                      <svg
                        onClick={!isSending ? sendMessage : undefined}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="#e5e5e5"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke={primaryColor}
                        className={`w-6 h-6 duration-300 ${
                          isSending
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                        />
                      </svg>
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={closeTicketConfirmation}
        setIsOpen={setCloseTicketConfirmation}
        message="Are you sure you want to close this ticket?"
        onConfirm={closeTicketHandler}
        isLoading={isCloseTicketLoading}
        loadingLabel="Closing ticket..."
      />
    </div>
  );
}

export default ViewTicket;
